/**
 * Admin Audit log — the human-readable audit surface (HUB-115).
 *
 * Lists single `tf_audit_event` rows from `GET /audit/events`: who did what,
 * when, on which record, with the old and new value a track reported. Usage
 * (`/admin/usage`) stays the bounded AGGREGATE surface (COP-134 D4); this
 * page is a list, not BI — no export, no charts.
 *
 * AUTHZ. `GET /audit/events` self-scopes: a caller reads their own rows unless
 * they hold `audit_log:global#viewer` or are a system admin (HUB-063 §4). The
 * route is therefore registered in an auth-only block in `App.tsx`, NOT under
 * `<RequireAdmin>` — a non-admin who opens it sees exactly their own actions.
 * Only the nav entry is admin-section (owner decision 2026-10-16). The track
 * and user pickers come from `/tracks` and `/users`; the latter is admin-only,
 * so it is requested only when the session's roles say admin — a non-admin
 * keeps the raw-UUID row and never fires a request certain to 403.
 *
 * This file owns ONLY the fetches. Rendering lives in `audit-view.tsx` and the
 * wording in `audit-ui-model.ts` so the unit tests never import
 * `@/lib/hub-client`.
 */
import { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '@refinedev/core';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import type { AuditEventListResponse } from '@verybigthings/trackframe-shared/schemas';
import type { ComboboxOption } from '@/components/ui/combobox';
import { hub } from '@/lib/hub-client';
import { buildPrincipalLabels } from '@/lib/principal-labels';
import { isAdminRoles } from '@/lib/require-admin';
import { useUrlState } from '@/lib/url-state';
import { usePrincipalLists } from '@/lib/use-principal-labels';
import { AUDIT_FILTER_DEFAULTS, buildAuditQuery } from './audit-ui-model';
import { AuditView, type AuditViewState } from './audit-view';

export function AuditLog() {
  const [filters, setFilters] = useUrlState(AUDIT_FILTER_DEFAULTS);
  const [state, setState] = useState<AuditViewState>({ kind: 'loading' });
  const { data: roles } = usePermissions<unknown>({});
  const { users, tracks } = usePrincipalLists({ users: isAdminRoles(roles) });

  // Any filter change invalidates the page — page 4 of the old result set is
  // meaningless in the new one.
  const onFilter = (patch: Partial<typeof AUDIT_FILTER_DEFAULTS>) =>
    setFilters({ ...patch, page: AUDIT_FILTER_DEFAULTS.page });
  const onPage = (pageIndex: number) => setFilters({ page: String(Math.max(0, pageIndex) + 1) });

  const query = buildAuditQuery(filters);
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    hub
      .auditEventList({ query })
      // Annotated, not cast: a contract change in the endpoint surfaces here.
      .then((res: AuditEventListResponse) => {
        if (!cancelled) setState({ kind: 'ready', data: res });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          httpStatus: err instanceof HubClientError ? err.httpStatus : null,
          message: (err as Error).message ?? 'Failed to load audit events',
        });
      });
    return () => {
      cancelled = true;
    };
    // `query` is rebuilt every render; the serialised key is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  const labels = useMemo(() => buildPrincipalLabels(users, tracks), [users, tracks]);
  const trackOptions = useMemo<ComboboxOption[]>(
    () => tracks.map((t) => ({ value: t.id, label: t.displayName?.trim() || t.name })),
    [tracks],
  );
  const userOptions = useMemo<ComboboxOption[]>(
    () => [...users].sort((a, b) => a.email.localeCompare(b.email)).map((u) => ({ value: u.id, label: u.email })),
    [users],
  );

  return (
    <AuditView
      state={state}
      filters={filters}
      onFilter={onFilter}
      onPage={onPage}
      trackOptions={trackOptions}
      userOptions={userOptions}
      labels={labels}
    />
  );
}
